import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ScrollIndicator from "../components/ScrollIndicator";

const ServicesPage = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Handle hash-based navigation
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [location]);

  const yagyaTypes = [
    { name: "Navagraha Shanti Yagya", when: "When multiple planets are afflicted — addresses all nine planetary energies together", tags: ["Planetary", "Video Proof"] },
    { name: "Lakshmi Yagya", when: "For persistent financial blockages — indicated when Venus or 2nd/11th house lords are afflicted", tags: ["Prosperity", "Video Proof"] },
    { name: "Mahamrityunjaya Yagya", when: "Health protection and longevity — one of the most ancient and powerful Vedic rituals", tags: ["Health", "Video Proof"] },
    { name: "Ganesh Yagya", when: "Obstacle removal — when Ketu or the 1st/8th house lords create repeated blockages", tags: ["Obstacles", "Video Proof"] },
    { name: "Pitru Paksha Yagya", when: "Ancestral healing — when Pitru Dosha is identified in the chart", tags: ["Ancestral", "Video Proof"] },
    { name: "Saraswati Yagya", when: "Education and intellectual clarity — when Mercury or the 4th/5th house is afflicted", tags: ["Education", "Video Proof"] },
    { name: "Rudra Abhishek", when: "Spiritual peace and inner clarity — when Saturn or the 12th house requires attention", tags: ["Spiritual", "Video Proof"] },
    { name: "Kaal Sarp Dosh Nivaran", when: "Specific to Kaal Sarp Dosha presence — Rahu-Ketu axis configuration", tags: ["Dosha", "Video Proof"] },
    { name: "Mangal Dosh Shanti", when: "Proportional remedy for Mangal Dosha — not all Mangal Dosha requires this, we will tell you honestly", tags: ["Dosha", "Video Proof"] },
    { name: "Dhan Prapti Yagya", when: "Wealth acquisition — specifically for blocked Dhan Yoga or malefic influence on wealth houses", tags: ["Wealth", "Video Proof"] }
  ];

  const comparisonRows = [
    ["Ritual verification", "No proof offered", "Video every time"],
    ["Before recommending a remedy", "Remedy first, explanation later", "Explanation first. Always."],
    ["Use of fear", "Fear used as a sales tool", "Prohibited. Zero tolerance."],
    ["Stone authenticity", "Synthetic stones, no documentation", "Natural stones, certificate included"],
    ["Yagya performers", "Generalists for all ceremonies", "Lineage-trained for each type"],
    ["When no remedy is needed", "Remedy suggested regardless", "Told honestly — no charge"],
    ["Remedy count", "Multiple pushed in sequence", "Only what chart indicates"],
    ["Outcome guarantees", "Outcomes guaranteed for a fee", "Honest — outcomes never guaranteed"],
    ["Bhojan Daan count", "Count unverifiable", "Photos + count documentation"],
    ["Birth time unknown", "Reading proceeds without flag", "Rectification process explained"]
  ];

  return (
    <div data-testid="services-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Vedic Astrology Services - Consultation, Anusthan, Bhoj & Daan | Amatya</title>
        <meta name="description" content="Explore Amatya's authentic Vedic astrology services - personalized consultations, sacred Anusthans, Bhoj & Daan rituals. Honest, transparent, and rooted in tradition." />
        <meta name="keywords" content="Vedic astrology services, astrology consultation, Anusthan, Bhoj Daan, horoscope reading, kundli analysis, spiritual guidance" />
        <link rel="canonical" href="https://www.amatya.co.in/services" />
        <meta property="og:title" content="Vedic Astrology Services - Amatya" />
        <meta property="og:description" content="Explore Amatya's authentic Vedic astrology services - personalized consultations, sacred Anusthans, Bhoj & Daan rituals." />
        <meta property="og:url" content="https://www.amatya.co.in/services" />
      </Helmet>
      
      {/* Hero */}
      <div className="page-hero" data-testid="services-hero">
        <div className="page-hero-content">
          <span className="page-hero-label">Our Services</span>
          <h1 className="page-hero-title">Every service. Fully explained.<br/><em>No surprises. No pressure.</em></h1>
          <p className="page-hero-sub">If you have been hurt before by dishonest astrologers, this page is written specifically for you. We name the fears. We show you exactly what we do — and exactly what we will never do.</p>
        </div>
      </div>

      {/* Burned Banner */}
      <div className="burned-banner" data-testid="burned-banner">
        <div className="wrap">
          <div className="burned-inner">
            <div className="burned-icon">🛡️</div>
            <div className="burned-text">
              <div className="burned-head">If you have been misled by an astrologer before — this is for you.</div>
              <div className="burned-body">We know the patterns: vague predictions to create anxiety, remedies pushed before explanations, rituals you cannot verify were ever performed, fees that escalate with your fear. We have spoken with hundreds of clients who experienced exactly this. We built Amatya as a direct response to it.</div>
              <div className="burned-pills">
                <span className="burned-pill">No fear tactics</span>
                <span className="burned-pill">No unexplained remedies</span>
                <span className="burned-pill">Every ritual documented</span>
                <span className="burned-pill">You decide. Always.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll to Services */}
      <div style={{background:'var(--smoke)'}}>
        <ScrollIndicator targetId="services-content" />
      </div>

      {/* Services Content */}
      <section className="services-content" id="services-content" data-testid="services-content">
        <div className="wrap">
          {/* Service Navigation Tiles */}
          <div className="service-nav-tiles reveal" style={{marginBottom:'40px'}}>
            {/* First Row - Astro Analysis - Centered */}
            <a href="#astro" className="service-nav-tile" data-testid="nav-tile-astro" style={{
              display:'flex', alignItems:'center', justifyContent:'center', gap:'24px', padding:'28px 40px',
              background:'linear-gradient(135deg, #2E221A 0%, #4A3324 100%)',
              border:'1px solid rgba(212,168,67,0.3)', textDecoration:'none', transition:'all 0.3s',
              cursor:'pointer', marginBottom:'20px'
            }}>
              <div style={{fontFamily:'Cinzel, serif', fontSize:'2.5rem', color:'rgba(212,168,67,0.5)', fontWeight:'600'}}>01</div>
              <div style={{textAlign:'center'}}>
                <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.65rem', letterSpacing:'0.18em', textTransform:'uppercase', color:'#C05C34', marginBottom:'8px'}}>The Foundation — Always First</div>
                <div style={{fontFamily:'Cinzel, serif', fontSize:'1.4rem', color:'#F0EAD6', letterSpacing:'0.04em', marginBottom:'8px'}}>Astro Analysis</div>
                <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#D4A843', fontWeight:'500'}}>₹ 2,101</div>
              </div>
            </a>
            
            {/* Second Row - 3 Services */}
            <div className="service-tiles-row" style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'20px', marginBottom:'20px'}}>
              <a href="#yagya" className="service-nav-tile" data-testid="nav-tile-yagya" style={{
                display:'flex', alignItems:'flex-start', gap:'16px', padding:'24px',
                background:'linear-gradient(135deg, #2E221A 0%, #4A3324 100%)',
                border:'1px solid rgba(212,168,67,0.3)', textDecoration:'none', transition:'all 0.3s',
                cursor:'pointer'
              }}>
                <div style={{fontFamily:'Cinzel, serif', fontSize:'2rem', color:'rgba(212,168,67,0.5)', fontWeight:'600', lineHeight:'1'}}>02</div>
                <div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.55rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'#C05C34', marginBottom:'8px', lineHeight:'1.4'}}>Add-On Service — Prescribed Only When Indicated</div>
                  <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#F0EAD6', letterSpacing:'0.03em', marginBottom:'8px'}}>Yagya Ceremonies</div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.85rem', color:'#D4A843'}}>₹2,100 – ₹51,000</div>
                </div>
              </a>
              <a href="#sankalpa" className="service-nav-tile" data-testid="nav-tile-sankalpa" style={{
                display:'flex', alignItems:'flex-start', gap:'16px', padding:'24px',
                background:'linear-gradient(135deg, #2E221A 0%, #4A3324 100%)',
                border:'1px solid rgba(212,168,67,0.3)', textDecoration:'none', transition:'all 0.3s',
                cursor:'pointer'
              }}>
                <div style={{fontFamily:'Cinzel, serif', fontSize:'2rem', color:'rgba(212,168,67,0.5)', fontWeight:'600', lineHeight:'1'}}>03</div>
                <div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.55rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'#C05C34', marginBottom:'8px', lineHeight:'1.4'}}>Sacred Vow Service — Deeply Personal</div>
                  <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#F0EAD6', letterSpacing:'0.03em', marginBottom:'8px'}}>Sankalpa</div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.85rem', color:'#D4A843'}}>₹501 – ₹2,100</div>
                </div>
              </a>
              <a href="#vastra" className="service-nav-tile" data-testid="nav-tile-vastra" style={{
                display:'flex', alignItems:'flex-start', gap:'16px', padding:'24px',
                background:'linear-gradient(135deg, #2E221A 0%, #4A3324 100%)',
                border:'1px solid rgba(212,168,67,0.3)', textDecoration:'none', transition:'all 0.3s',
                cursor:'pointer'
              }}>
                <div style={{fontFamily:'Cinzel, serif', fontSize:'2rem', color:'rgba(212,168,67,0.5)', fontWeight:'600', lineHeight:'1'}}>04</div>
                <div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.55rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'#C05C34', marginBottom:'8px', lineHeight:'1.4'}}>Sacred Charity — Karmic Merit</div>
                  <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#F0EAD6', letterSpacing:'0.03em', marginBottom:'8px'}}>Vastra Daan</div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.85rem', color:'#D4A843'}}>₹1,100 – ₹11,000</div>
                </div>
              </a>
            </div>
            
            {/* Third Row - Bhojan Daan Centered */}
            <div style={{display:'flex', justifyContent:'center'}}>
              <a href="#bhojan" className="service-nav-tile service-tile-bhojan" data-testid="nav-tile-bhojan" style={{
                display:'flex', alignItems:'flex-start', gap:'16px', padding:'24px',
                background:'linear-gradient(135deg, #2E221A 0%, #4A3324 100%)',
                border:'1px solid rgba(212,168,67,0.3)', textDecoration:'none', transition:'all 0.3s',
                cursor:'pointer', width:'33%', minWidth:'300px'
              }}>
                <div style={{fontFamily:'Cinzel, serif', fontSize:'2rem', color:'rgba(212,168,67,0.5)', fontWeight:'600', lineHeight:'1'}}>05</div>
                <div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.55rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'#C05C34', marginBottom:'8px', lineHeight:'1.4'}}>Most Powerful Karmic Act — Mass Feeding</div>
                  <div style={{fontFamily:'Cinzel, serif', fontSize:'1.1rem', color:'#F0EAD6', letterSpacing:'0.03em', marginBottom:'8px'}}>Bhojan Daan</div>
                  <div style={{fontFamily:'Lato, sans-serif', fontSize:'0.85rem', color:'#D4A843'}}>₹16,000 – ₹56,000</div>
                </div>
              </a>
            </div>
          </div>

          {/* SERVICE 1: ASTRO ANALYSIS */}
          <div className="svc reveal" id="astro" data-testid="service-astro">
            <div className="svc-header">
              <div className="svc-title-group">
                <span className="svc-tag">The Foundation — Always First</span>
                <h2 className="svc-title">Astro Analysis</h2>
              </div>
              <div className="svc-num">01</div>
              <div className="svc-price-badge">₹ 2,101</div>
            </div>

            <div className="fear-box">
              <div className="fb-head">⚠ What burned clients have experienced elsewhere</div>
              <div className="fb-items">
                <div className="fb-item">A 10-minute reading that told them catastrophic things — designed to panic, not to illuminate</div>
                <div className="fb-item">Generic predictions that could apply to anyone — not personalised to their actual birth data</div>
                <div className="fb-item">A reading that ended immediately in an expensive remedy — before any explanation was offered</div>
                <div className="fb-item">Predictions about death, disaster, or ruin — used as leverage to extract money</div>
              </div>
            </div>

            <div className="safe-box">
              <div className="sb-head">✓ What you will experience with Amatya</div>
              <div className="sb-items">
                <div className="sb-item">A deep, personalised chart study — minimum 4 hours of analysis by an experienced Jyotish practitioner</div>
                <div className="sb-item">Written report delivered to you — clear language, no jargon, no scary terminology</div>
                <div className="sb-item">Explanation of what the chart shows before any remedy is ever mentioned</div>
                <div className="sb-item">If no significant challenge is found, we will tell you that — and charge you nothing additional</div>
              </div>
            </div>

            <p className="svc-desc">Your birth chart is a map — precise, unique to you, and full of information. We read it as a trained navigator would: carefully, honestly, and in full detail. The analysis identifies the root planetary configuration behind your primary life challenge — not a surface symptom, but the underlying cause.</p>

            <div className="honest-caveat">
              <div className="hc-label">Our Honest Position on This Service</div>
              <div className="hc-text">The analysis will tell you what the chart shows — not what you want to hear. Sometimes a chart shows genuine challenges. We will describe them calmly and in context, never with alarm. If you decide after reading the analysis that you do not wish to proceed with any remedy, that is entirely your right. The analysis is complete in itself.</div>
            </div>

            <div className="pull-quote">
              <div className="pq-quote">"</div>
              <p className="pq-text">Three other astrologers told me terrible things were coming and that I needed expensive rituals immediately. Amatya's analysis simply explained what the chart showed — calmly, clearly, in writing. I could read it, think about it, and decide. Nothing was pushed on me.</p>
              <span className="pq-author">Rahul M., Pune</span>
              <span className="pq-area">Career & Direction</span>
            </div>

            <div className="svc-cta">
              <Link to="/begin-journey" className="btn btn-ember">Begin Your Analysis <span className="btn-arr">→</span></Link>
              <a href="https://wa.me/919711767167" className="btn btn-ghost" target="_blank" rel="noopener noreferrer">Speak to Us First</a>
            </div>
          </div>

          {/* SERVICE 2: YAGYA */}
          <div className="svc reveal" id="yagya" data-testid="service-yagya">
            <div className="svc-header">
              <div className="svc-title-group">
                <span className="svc-tag">Add-On Service — Prescribed Only When Indicated</span>
                <h2 className="svc-title">Yagya Ceremonies</h2>
              </div>
              <div className="svc-num">02</div>
              <div className="svc-price-badge">₹2,100 – ₹51,000</div>
            </div>

            <div className="fear-box">
              <div className="fb-head">⚠ What burned clients have experienced elsewhere</div>
              <div className="fb-items">
                <div className="fb-item">Told a Yagya was performed — with no video, no photos, and no way to verify</div>
                <div className="fb-item">Pushed toward expensive Yagyas in the first 5 minutes of a consultation, before any real chart analysis</div>
                <div className="fb-item">Performers with no verified lineage or training in the specific ritual type</div>
                <div className="fb-item">Multiple Yagyas recommended in succession — escalating fees with no explanation of why each was needed</div>
              </div>
            </div>

            <div className="safe-box">
              <div className="sb-head">✓ What you will experience with Amatya</div>
              <div className="sb-items">
                <div className="sb-item">A Yagya is suggested only after your chart analysis indicates a specific planetary imbalance that warrants it</div>
                <div className="sb-item">We explain which Yagya, why that Yagya, and what it addresses — before you commit to anything</div>
                <div className="sb-item">Performers are lineage-trained in the specific ritual type — we do not use general performers for specialised ceremonies</div>
                <div className="sb-item">Full video recording delivered to you — you will see your name spoken, the ceremony performed, the fire lit</div>
              </div>
            </div>

            <p className="svc-desc">A Yagya is a Vedic fire ritual — one of the most powerful forms of planetary remedy available in the tradition. Each type addresses a specific planetary configuration, and each requires a specifically trained performer. These are not interchangeable ceremonies performed by a generalist. We take the authenticity of this seriously.</p>

            <div className="proof-strip">
              <div className="ps-icon">🎥</div>
              <div className="ps-content">
                <div className="ps-title">Full Video Documentation — Without Exception</div>
                <div className="ps-desc">Every Yagya performed through Amatya is recorded in full. You receive a secure link to the video within 48 hours of completion. You will hear your name and Gotra invoked. You will see the ceremony from beginning to end. This is not optional — it is our standard.</div>
              </div>
            </div>

            <div className="yagya-wrap">
              <div className="svc-includes-title">Available Yagya Types (Prescribed by Chart Indication Only)</div>
              <div className="yagya-grid">
                {yagyaTypes.map((y, i) => (
                  <div key={i} className="yc" data-testid={`yagya-type-${i}`}>
                    <div className="yc-name">{y.name}</div>
                    <div className="yc-when">{y.when}</div>
                    <div className="yc-tags">
                      {y.tags.map((t, j) => <span key={j} className="yc-tag">{t}</span>)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="brahmins-note">
                <strong style={{color:'var(--ink)'}}>Number of Brahmins:</strong> 1 to 11, based on the severity of the planetary indication and the specific Yagya type. We will recommend the appropriate number based on your chart — we will not automatically suggest 11 when 1 is genuinely sufficient.
              </div>
            </div>

            <div className="honest-caveat">
              <div className="hc-label">Our Position on Yagya Efficacy</div>
              <div className="hc-text">A Yagya creates conditions — energetic, intentional, and spiritual — that support resolution. It does not override karma or guarantee outcomes. Anyone who tells you a Yagya guarantees a specific result is not being honest with you. We perform these ceremonies with full authenticity and care. Results unfold as they are meant to.</div>
            </div>

            <div className="pull-quote">
              <div className="pq-quote">"</div>
              <p className="pq-text">I was deeply sceptical of online astrology. Amatya changed that. I received the full video of the Yagya — I heard my own name and Gotra spoken clearly. It gave me a peace I had not felt in years.</p>
              <span className="pq-author">Arjun K., Delhi</span>
              <span className="pq-area">Sceptic Converted</span>
            </div>

            <div className="svc-cta">
              <Link to="/begin-journey" className="btn btn-ember">Begin With Chart Analysis First <span className="btn-arr">→</span></Link>
            </div>
          </div>

          {/* SERVICE 3: SANKALPA */}
          <div className="svc reveal" id="sankalpa" data-testid="service-sankalpa">
            <div className="svc-header">
              <div className="svc-title-group">
                <span className="svc-tag">Sacred Vow Service — Deeply Personal</span>
                <h2 className="svc-title">Sankalpa</h2>
              </div>
              <div className="svc-num">03</div>
              <div className="svc-price-badge">₹501 – ₹2,100</div>
            </div>

            <p className="svc-desc">A Sankalpa is a sacred vow — a formal, spiritually binding statement of intention performed by a qualified Brahmin using your name and your Gotra. It is one of the most personal of all Vedic services, because it invokes your specific lineage and places your intention within the sacred framework of the tradition.</p>

            <div className="safe-box">
              <div className="sb-head">✓ Our Sankalpa Standard</div>
              <div className="sb-items">
                <div className="sb-item">Your name and Gotra are spoken aloud — correctly, with proper pronunciation, confirmed before the ceremony</div>
                <div className="sb-item">Video recording delivered to you — you will hear yourself named</div>
                <div className="sb-item">Performed by a Brahmin specifically trained in Sankalpa recitation and the associated Vedic protocol</div>
                <div className="sb-item">If you do not know your Gotra, we will guide you on how to determine it — or use Kashyap Gotra by convention with your knowledge and consent</div>
              </div>
            </div>

            <div className="proof-strip">
              <div className="ps-icon">🎥</div>
              <div className="ps-content">
                <div className="ps-title">Your Name. Spoken. On Camera.</div>
                <div className="ps-desc">The most meaningful aspect of a Sankalpa is that it is personal to you. Hearing your name invoked in the sacred context — in the Vedic tradition, by a trained practitioner — is a deeply moving experience for most clients. You will receive this as a video you can keep.</div>
              </div>
            </div>

            <div className="svc-cta">
              <Link to="/begin-journey" className="btn btn-ember">Begin With Chart Analysis <span className="btn-arr">→</span></Link>
            </div>
          </div>

          {/* SERVICE 4: VASTRA DAAN */}
          <div className="svc reveal" id="vastra" data-testid="service-vastra">
            <div className="svc-header">
              <div className="svc-title-group">
                <span className="svc-tag">Sacred Charity — Karmic Merit</span>
                <h2 className="svc-title">Vastra Daan</h2>
              </div>
              <div className="svc-num">04</div>
              <div className="svc-price-badge">₹1,100 – ₹11,000</div>
            </div>

            <p className="svc-desc">Vastra Daan is the donation of clothing — five items of Vastra (clothing) donated on your behalf to Brahmin recipients, performed with full Vedic ritual. In the Vedic understanding, charitable acts performed with pure intention generate karmic merit that helps balance planetary afflictions and open blocked pathways.</p>

            <div className="safe-box">
              <div className="sb-head">✓ How We Perform Vastra Daan</div>
              <div className="sb-items">
                <div className="sb-item">New, quality clothing items — never used or donated goods repackaged</div>
                <div className="sb-item">Performed with full Vedic ritual protocol, including Sankalpa with your name</div>
                <div className="sb-item">Photographs of the ceremony and donation shared with you</div>
                <div className="sb-item">Number of Brahmins (1–11) determined by chart indication, not by the desire to charge more</div>
              </div>
            </div>

            <div className="proof-strip">
              <div className="ps-icon">📸</div>
              <div className="ps-content">
                <div className="ps-title">Photo Documentation Delivered</div>
                <div className="ps-desc">Photographs of the ceremony, the recipients, and the ritual process are delivered to you after completion. You will see that your donation was made — and made with care.</div>
              </div>
            </div>

            <div className="svc-cta">
              <Link to="/begin-journey" className="btn btn-ember">Begin With Chart Analysis <span className="btn-arr">→</span></Link>
            </div>
          </div>

          {/* SERVICE 5: BHOJAN DAAN */}
          <div className="svc reveal" id="bhojan" data-testid="service-bhojan">
            <div className="svc-header">
              <div className="svc-title-group">
                <span className="svc-tag">Most Powerful Karmic Act — Mass Feeding</span>
                <h2 className="svc-title">Bhojan Daan</h2>
              </div>
              <div className="svc-num">05</div>
              <div className="svc-price-badge">₹16,000 – ₹56,000</div>
            </div>

            <p className="svc-desc">Bhojan Daan — feeding the hungry — is considered among the highest karmic acts in the Vedic tradition. Food offered with sincere intention to 250, 500, or 1,000 people generates profound karmic merit. It is prescribed when the chart indicates significant ancestral karma, Pitru Dosha, or major Saturn afflictions that require an act of corresponding weight to balance.</p>

            <div className="safe-box">
              <div className="sb-head">✓ How We Perform Bhojan Daan</div>
              <div className="sb-items">
                <div className="sb-item">Performed at genuine community feeding events — real people, real food, real merit</div>
                <div className="sb-item">Your name and Gotra invoked in Sankalpa before the feeding begins</div>
                <div className="sb-item">Photographs and count of people fed delivered to you as documentation</div>
                <div className="sb-item">We do not inflate headcounts — the number you pay for is the number who are fed</div>
              </div>
            </div>

            <table className="daan-table">
              <thead>
                <tr>
                  <th>Package</th>
                  <th>250 People</th>
                  <th>500 People</th>
                  <th>1,000 People</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Aloo Sabzi + Poori</td><td>₹ 16,000</td><td>₹ 25,000</td><td>₹ 40,000</td></tr>
                <tr><td>Aloo Sabzi + Poori + Halwa</td><td>₹ 20,000</td><td>₹ 33,000</td><td>₹ 56,000</td></tr>
              </tbody>
            </table>

            <div className="proof-strip">
              <div className="ps-icon">📸</div>
              <div className="ps-content">
                <div className="ps-title">Photo Documentation — Headcount Verified</div>
                <div className="ps-desc">You receive photographs from the event, confirmation of your Sankalpa invocation, and documentation of the number of people served. The event is real. The merit is genuine. The proof is yours to keep.</div>
              </div>
            </div>

            <div className="honest-caveat">
              <div className="hc-label">Why We Prescribe This Carefully</div>
              <div className="hc-text">Bhojan Daan is a significant investment. We recommend it only when the chart clearly indicates that an act of this weight and intention is genuinely needed. If a smaller remedy is sufficient, we will tell you that. We will never suggest Bhojan Daan simply because it represents our highest price point.</div>
            </div>

            <div className="pull-quote">
              <div className="pq-quote">"</div>
              <p className="pq-text">I had been told by three different astrologers to do Bhojan Daan for 1,000 people with no explanation of why. Amatya looked at my chart and told me 250 was what was indicated — and explained exactly which affliction it was addressing. The photos arrived within two days.</p>
              <span className="pq-author">Suresh V., Mumbai</span>
              <span className="pq-area">Financial Challenges</span>
            </div>

            <div className="svc-cta">
              <Link to="/begin-journey" className="btn btn-ember">Begin With Chart Analysis First <span className="btn-arr">→</span></Link>
              <a href="https://wa.me/919711767167" className="btn btn-ghost" target="_blank" rel="noopener noreferrer">Ask About Bhojan Daan</a>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="compare-section" data-testid="compare-section">
        <div className="wrap-n">
          <div className="reveal" style={{textAlign:'center',marginBottom:'8px'}}>
            <span style={{fontFamily:'var(--ff-s)',fontSize:'.62rem',letterSpacing:'.3em',textTransform:'uppercase',color:'var(--gold)'}}>The Difference</span>
          </div>
          <h2 style={{fontFamily:'var(--ff-d)',fontSize:'clamp(1.6rem,3vw,2.4rem)',color:'var(--ember)',letterSpacing:'.05em',textAlign:'center'}} className="reveal">What sets us apart — plainly stated</h2>
          <p style={{fontFamily:'var(--ff-b)',fontStyle:'italic',fontSize:'1rem',lineHeight:'1.85',color:'var(--ink-soft)',textAlign:'center',maxWidth:'580px',margin:'16px auto 0'}} className="reveal d1">We name these differences not to attack others, but because you deserve to know what to look for — and what to walk away from.</p>

          <table className="compare-table reveal d2">
            <thead>
              <tr>
                <th>What to look for</th>
                <th>⚠ Common Elsewhere</th>
                <th>✓ Amatya Standard</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={i}>
                  <td>{row[0]}</td>
                  <td>{row[1]}</td>
                  <td>{row[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Bottom CTA */}
      <div className="bottom-cta" data-testid="services-bottom-cta">
        <div style={{position:'relative',zIndex:2}} className="reveal">
          <span className="bc-dev">॥ ॐ गं गणपतये नमः ॥</span>
          <h2 className="bc-title">Ready to begin — <em>without fear</em></h2>
          <p className="bc-body">Start with the chart analysis. Understand what it shows. Decide what, if anything, to do about it. No pressure at any point. No surprises.</p>
          <div className="bc-btns">
            <Link to="/begin-journey" className="btn btn-ember">Begin Your Analysis — ₹2,101 <span className="btn-arr">→</span></Link>
            <a href="https://wa.me/919711767167" className="btn btn-ghost-lt" target="_blank" rel="noopener noreferrer">Speak to Us on WhatsApp</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
